import React, { useEffect, useState } from 'react';

import { Box, IconButton, Menu, MenuItem, Tooltip } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import i18n from 'i18n-js';

import { ReservationDTO } from 'api/controllers/reservations';
import { PopupConfirmation } from 'components/shared';
import {
    formatGuestName,
    formatGuestsCount,
    formatGuestsLabel,
    formatReservationDate,
    getReservationFilterKey,
    isRequestOverdue,
} from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import globalStore from 'store/globalStore';
import reservationsStore from 'store/reservationsStore';
import {
    ReservationsTabFilter,
    useAppliedReservationFilter,
    useReservationMaps,
    useRestaurantWallClock,
    useThunkDispatch,
} from '../hooks';
import ReservationDetailsPanel, { OverdueChip } from './ReservationDetailsPanel';
import ReservationDaySummary from './ReservationDaySummary';
import ReservationsFilterBar from './ReservationsFilterBar';
import ReservationsTable, { ReservationColumn } from './ReservationsTable';
import RequestActions from './RequestActions';

interface RequestsTabProps {
    filter: ReservationsTabFilter;
    onFilterChange: (filter: ReservationsTabFilter) => void;
    pinnedDay: string | null;
    onPinChange: (day: string | null) => void;
}

export default function RequestsTab({
    filter,
    onFilterChange,
    pinnedDay,
    onPinChange,
}: RequestsTabProps) {
    const dispatch = useThunkDispatch();
    const { requests, selectedRequestId, requestsView } = useAppSelector(
        (state) => state.reservations
    );
    const restaurantId = useAppSelector((state) => state.user.currentRestaurantId);
    const isRequestsLoaded = useAppSelector(
        (state) => state.reservations.requestsRestaurantId === state.user.currentRestaurantId
    );
    const { tables } = useReservationMaps();
    const [archiveMenu, setArchiveMenu] =
        useState<{
            anchor: HTMLElement;
            request: ReservationDTO;
        } | null>(null);
    const [archiveTarget, setArchiveTarget] = useState<ReservationDTO | null>(null);
    const [isArchiving, setIsArchiving] = useState(false);
    const { applied, isDebouncing } = useAppliedReservationFilter(filter);
    const appliedKey = getReservationFilterKey(applied);
    const isFiltered = !!requestsView.filter;
    const rows = isFiltered ? requestsView.items : requests;
    const isLoading = !isRequestsLoaded || requestsView.isLoading;
    const isShownFilterApplied =
        !isLoading &&
        (requestsView.filter ? getReservationFilterKey(requestsView.filter) : '|') === appliedKey;
    const selected = rows.find((request) => request.id === selectedRequestId);
    const wallClock = useRestaurantWallClock();

    useEffect(() => {
        dispatch(reservationsStore.actionCreators.loadRequestsView(applied));
    }, [appliedKey, restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isShownFilterApplied && selectedRequestId && !selected) {
            dispatch(reservationsStore.actionCreators.selectRequest(null));
        }
    }, [isShownFilterApplied, selectedRequestId, !!selected]); // eslint-disable-line react-hooks/exhaustive-deps

    const isOverdue = (request: ReservationDTO) => isRequestOverdue(request, wallClock);

    const columns: ReservationColumn[] = [
        {
            id: 'dateTime',
            label: i18n.t('reservations.columns.dateTime'),
            getText: (request) =>
                formatReservationDate(request.reservationStartDateTime, 'dd.MM · HH:mm'),
            getSortValue: (request) => request.reservationStartDateTime,
            render: (request) => (
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <span>
                        {formatReservationDate(request.reservationStartDateTime, 'dd.MM · HH:mm')}
                    </span>
                    {isOverdue(request) && <OverdueChip />}
                </Box>
            ),
        },
        { id: 'name', label: i18n.t('reservations.columns.name'), getText: formatGuestName },
        {
            id: 'guests',
            label: i18n.t('reservations.columns.guests'),
            getText: formatGuestsCount,
            getSortValue: (request) => request.guestsNumber,
        },
        {
            id: 'contact',
            label: i18n.t('reservations.columns.contact'),
            getText: (request) => request.phone || request.email || '',
        },
        {
            id: 'source',
            label: i18n.t('reservations.columns.source'),
            getText: (request) => i18n.t(`reservations.channels.${request.channel}`),
        },
        {
            id: 'received',
            label: i18n.t('reservations.columns.received'),
            getText: (request) => formatReservationDate(request.createDate, 'dd.MM HH:mm'),
            getSortValue: (request) => request.createDate,
        },
    ];

    const handleArchive = async (id: string) => {
        if (isArchiving) {
            return;
        }
        setIsArchiving(true);
        try {
            const result = await dispatch(reservationsStore.actionCreators.archiveRequest(id));
            dispatch(
                result === 'archived'
                    ? globalStore.actionCreators.showToaster(
                          'success',
                          i18n.t('reservations.requestArchived')
                      )
                    : globalStore.actionCreators.showToaster(
                          'error',
                          i18n.t('reservations.alreadyHandled')
                      )
            );
            setArchiveTarget(null);
        } catch {
            dispatch(
                globalStore.actionCreators.showToaster(
                    'error',
                    i18n.t('reservations.archiveFailed')
                )
            );
        } finally {
            setIsArchiving(false);
        }
    };

    return (
        <Box display="flex" flexDirection="column" flex={1} minHeight={0} pl={3}>
            <ReservationsFilterBar
                value={filter}
                onChange={onFilterChange}
                isLoading={isDebouncing || isLoading}
                summary={filter.day ? <ReservationDaySummary day={filter.day} /> : null}
                pinnedDay={pinnedDay}
                onPinChange={onPinChange}
            />
            <Box display="flex" flex={1} minHeight={0}>
                <ReservationsTable
                    columns={columns}
                    reservations={rows}
                    sortLast={isOverdue}
                    renderRowActions={(request) =>
                        isOverdue(request) ? (
                            <Tooltip title={i18n.t('reservations.moreActions')}>
                                <IconButton
                                    size="small"
                                    aria-label={i18n.t('reservations.moreActions')}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setArchiveMenu({ anchor: event.currentTarget, request });
                                    }}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            </Tooltip>
                        ) : null
                    }
                    selectedId={selectedRequestId}
                    onSelect={(id) => dispatch(reservationsStore.actionCreators.selectRequest(id))}
                    emptyText={
                        isFiltered
                            ? i18n.t('reservations.filters.noResults')
                            : i18n.t('reservations.noOpenRequests')
                    }
                    isLoading={isLoading}
                    pageResetKey={appliedKey}
                />
                {selected && (
                    <ReservationDetailsPanel
                        reservation={selected}
                        isOverdue={isOverdue(selected)}
                        tables={tables}
                        onClose={() =>
                            dispatch(reservationsStore.actionCreators.selectRequest(null))
                        }
                        footer={<RequestActions key={selected.id} reservation={selected} />}
                    />
                )}
            </Box>
            <Menu
                anchorEl={archiveMenu?.anchor}
                open={!!archiveMenu}
                onClose={() => setArchiveMenu(null)}
            >
                <MenuItem
                    onClick={() => {
                        setArchiveTarget(archiveMenu?.request ?? null);
                        setArchiveMenu(null);
                    }}
                >
                    {i18n.t('reservations.moveToArchive')}
                </MenuItem>
            </Menu>
            {archiveTarget && (
                <PopupConfirmation
                    open
                    isDismissible
                    close={() => setArchiveTarget(null)}
                    title={i18n.t('reservations.archiveTitle')}
                    description={i18n.t('reservations.archiveDescription', {
                        name: formatGuestName(archiveTarget),
                        dateTime: formatReservationDate(
                            archiveTarget.reservationStartDateTime,
                            'EEE dd.MM.yyyy, HH:mm'
                        ),
                        guests: formatGuestsLabel(formatGuestsCount(archiveTarget)),
                    })}
                    activeBtn={i18n.t('reservations.moveToArchiveButton')}
                    isSubmitting={isArchiving}
                    keepOpenOnAction
                    preventDismissWhileSubmitting
                    action={() => handleArchive(archiveTarget.id)}
                />
            )}
        </Box>
    );
}
